import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Paperclip, FileText, Camera, Video } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const SID_KEY = "rudram_anxya_sid";
function sid() {
  let s = localStorage.getItem(SID_KEY);
  if (!s) { s = "sess_" + Math.random().toString(36).slice(2, 12); localStorage.setItem(SID_KEY, s); }
  return s;
}

export default function AnXya() {
  const { user, setUser } = useApp();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: "Namaste 🙏 I'm ANXYA — your assistant for everything. Build for any industry or government, interpret documents, plan your personal life. Type a message or upload a file and I'll help. What shall we do today?" },
  ]);
  const endRef = useRef(null);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const sessionId = useRef(sid());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);
  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("open-anxya", openHandler);
    return () => window.removeEventListener("open-anxya", openHandler);
  }, []);

  const send = async () => {
    const text = input.trim();
    if ((!text && !file) || busy) return;
    const attached = file;
    setInput(""); setFile(null);
    setMsgs((m) => [...m, { role: "user", content: (text || "") + (attached ? `  📎 ${attached.name}` : "") }]);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("session_id", sessionId.current);
      fd.append("message", text);
      if (attached) fd.append("file", attached);
      const { data } = await api.post("/anxya/chat", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMsgs((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.credits_remaining != null && user) setUser({ ...user, credits: data.credits_remaining });
      const costNote = data.cost ? ` · ${data.cost} credit${data.cost > 1 ? "s" : ""}` : "";
      toast("ANXYA replied" + costNote, { description: data.reply.slice(0, 80) + (data.reply.length > 80 ? "…" : "") });
    } catch {
      setMsgs((m) => [...m, { role: "assistant", content: "I hit a snag reaching my core. Please try again shortly." }]);
    } finally {
      setBusy(false);
    }
  };

  const startMeet = () => {
    const room = `ANXYA-${sessionId.current}`;
    const link = `${window.location.origin}/meet?room=${encodeURIComponent(room)}`;
    setMsgs((m) => [...m, { role: "assistant", content: `🎥 A secure meeting room has been architected. Join now for live collaboration.\n${link}` }]);
    window.open(`/meet?room=${encodeURIComponent(room)}`, "_blank");
    toast.success("Secure room ready — invite link added to the chat.");
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            className="fixed bottom-24 right-4 sm:right-6 z-[60] w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col glass rounded-2xl overflow-hidden shadow-2xl"
            data-testid="anxya-panel"
          >
            <div className="relative p-4 border-b border-rud flex items-center gap-3">
              <div className="absolute -top-6 -left-6 w-24 h-24 brand-glow rounded-full pointer-events-none" />
              <div className="w-9 h-9 rounded-full bg-brand grid place-items-center text-black font-bold">a</div>
              <div>
                <div className="font-serif text-xl leading-none">ANXYA</div>
                <div className="text-xs text-sec flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" /> Autonomous guide</div>
              </div>
              <button onClick={startMeet} data-testid="anxya-video-meet" title="Start a secure video meeting" className="ml-auto btn-ghost rounded-full h-8 px-3 text-xs flex items-center gap-1.5"><Video size={14} /> Video Meet</button>
              <button onClick={() => setOpen(false)} className="text-sec hover:text-[var(--rud-text)]" data-testid="anxya-close"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-brand text-black" : "bg-elevated text-[var(--rud-text)] border border-rud"}`}>
                    {m.content.replace(/\*\*(.*?)\*\*/g, "$1").replace(/\*(.*?)\*/g, "$1")}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-elevated border border-rud rounded-2xl px-4 py-3 flex gap-1">
                    <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[var(--rud-text-sec)]" />
                    <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[var(--rud-text-sec)]" />
                    <span className="typing-dot w-1.5 h-1.5 rounded-full bg-[var(--rud-text-sec)]" />
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-rud">
              {file && (
                <div className="flex items-center gap-1.5 mb-2 text-xs text-sec bg-elevated border border-rud rounded-full px-3 py-1.5 w-fit max-w-full" data-testid="anxya-file-chip">
                  <FileText size={13} className="text-brand shrink-0" />
                  <span className="truncate">{file.name}</span>
                  <button onClick={() => setFile(null)} className="hover:text-red-400 shrink-0"><X size={13} /></button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" data-testid="anxya-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <input ref={cameraRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" data-testid="anxya-camera-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <button data-testid="anxya-attach" onClick={() => fileRef.current?.click()} disabled={busy}
                  className="btn-ghost rounded-full h-10 w-10 grid place-items-center shrink-0 disabled:opacity-50" title="Attach a file">
                  <Paperclip size={16} />
                </button>
                <button data-testid="anxya-camera-btn" onClick={() => cameraRef.current?.click()} disabled={busy}
                  className="btn-ghost rounded-full h-10 w-10 grid place-items-center shrink-0 disabled:opacity-50" title="Take a photo or record a video">
                  <Camera size={16} />
                </button>
                <input
                  data-testid="anxya-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask ANXYA, or attach a file…"
                  className="rud-input flex-1 rounded-full px-4 py-2.5 text-sm"
                />
                <button data-testid="anxya-send" onClick={send} disabled={busy} className="btn-primary h-10 w-10 grid place-items-center shrink-0 disabled:opacity-50">
                  <Send size={16} />
                </button>
              </div>
              <p className="text-[10px] text-sec mt-1.5 px-1">Word · PDF · PPT · Excel · images · video. Small credit cost — scales with file size & length.</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-5 right-4 sm:right-6 z-[60] flex flex-col items-end gap-1">
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="anxya-toggle"
          className="btn-primary h-11 px-4 rounded-full flex items-center gap-1.5 shadow-2xl"
        >
          {open ? <X size={18} /> : <><Sparkles size={15} /> <span className="font-serif text-sm">Ask ANXYA</span></>}
        </button>
        {!open && <span className="text-[9px] text-sec pr-2 tracking-wide">your personal assistant</span>}
      </div>
    </>
  );
}
